import React, { useState, useEffect } from "react";
import {
  Card,
  Alert,
  Typography,
  Button,
  Descriptions,
  Divider,
  Spin,
  Tag,
  List,
  Row,
  Col,
  Table,
  InputNumber
} from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Radar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);
const { Title, Text, Paragraph } = Typography;
const quadrantMap = {
  1: {
    label: "DOMINANCE (D)",
    color: "#e74c3c",
    bgColor: "rgba(231, 76, 60, 0.05)",
    traits: ["Focused", "Inspirational", "Independent"],
  },
  2: {
    label: "INFLUENCE (I)",
    color: "#2ecc71",
    bgColor: "rgba(46, 204, 113, 0.05)",
    traits: ["Energetic", "Sociable", "Positive"],
  },
  3: {
    label: "STEADINESS (S)",
    color: "#3498db",
    bgColor: "rgba(52, 152, 219, 0.05)",
    traits: ["Patience", "Persistence", "Thoughtfulness"],
  },
  4: {
    label: "COMPLIANCE (C)",
    color: "#f1c40f",
    bgColor: "rgba(241, 196, 15, 0.05)",
    traits: ["Structure", "Organization", "Data-driven"],
  },
};
const PerformanceReviewDISC = () => {
  const location = useLocation();
  const learnerId = location.state?.learnerId;
  const firstName = location.state?.firstName;
  const lastName = location.state?.lastName;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    const fetchCombinedReport = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/PerformanceEvaluation/Learner/PerformanceAndPersonality-Report`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params: { learnerId }
          }
        );
        if (!response.data) {
          throw new Error("No data received from API");
        }
        setReport(response.data);
      } catch (err) {
        console.error("API Error:", err);
        setError(err.response?.data?.message || err.message || "Failed to load reports");
      } finally {
        setLoading(false);
      }
    };
    if (learnerId) {
      fetchCombinedReport();
    }
  }, [learnerId]);
  // Prepare radar chart data for performance factors
  const getPerformanceFactorsData = () => {
    if (!report?.performanceReview?.performanceFactors) return null;
    const factors = report.performanceReview.performanceFactors;
    const labels = [
      "Job Knowledge",
      "Administration",
      "Decision Making",
      "Communication",
      "Initiative",
      "Work Relationships",
      "Flexibility",
      "Decision Making",
      "Communication"
    ];
    const data = [
      factors.jobKnowledgeTechnicalSkillsRating,
      factors.administrationRating,
      factors.decisionMakingRating,
      factors.communicationRating,
      factors.initiativeResponsibilityRating,
      factors.workRelationshipsRating,
      factors.flexibilityRating,
      factors.decisionMakingRating,
      factors.communicationRating
    ];
    return {
      labels,
      datasets: [{
        label: 'Performance Factors',
        data,
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 2,
        pointBackgroundColor: 'rgba(54, 162, 235, 1)',
        pointRadius: 4
      }]
    };
  };
  // Prepare radar chart data for skills
  const getSkillsData = () => {
    if (!report?.performanceReview?.accountabilityGoals?.length) return null;
    const skills = report.performanceReview.accountabilityGoals
      .filter(goal => goal.learnerSkillName)
      .map(goal => ({
        name: goal.learnerSkillName,
        rating: goal.rating
      }));
    if (skills.length === 0) return null;
    return {
      labels: skills.map(skill => skill.name),
      datasets: [{
        label: 'Skills Rating',
        data: skills.map(skill => skill.rating),
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 2,
        pointBackgroundColor: 'rgba(75, 192, 192, 1)',
        pointRadius: 4
      }]
    };
  };
  const radarOptions = {
    responsive: true,
    scales: {
      r: {
        angleLines: { display: true },
        suggestedMin: 1,
        suggestedMax: 5,
        ticks: { stepSize: 1 }
      }
    },
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.raw}`
        }
      }
    }
  };
  // Performance Evaluation Summary data
  const summaryData = report?.performanceReview ? [
    {
      key: '1',
      section: 'I',
      description: 'Overall Accountability/Goal Rating',
      percentage: '50%',
      score: typeof report.performanceReview.weightedAccountabilityScore === 'object'
        ? report.performanceReview.weightedAccountabilityScore.numericValue
        : report.performanceReview.weightedAccountabilityScore,
      maxScore: 50,
      ratingCode: report.performanceReview.performanceRatingCode?.code ?
        `${report.performanceReview.performanceRatingCode.code} (${report.performanceReview.performanceRatingCode.name})` :
        'N/A'
    },
    {
      key: '2',
      section: 'II',
      description: 'Overall Performance Factors Rating',
      percentage: '40%',
      score: typeof report.performanceReview.weightedPerformanceFactorsScore === 'object'
        ? report.performanceReview.weightedPerformanceFactorsScore.numericValue
        : report.performanceReview.weightedPerformanceFactorsScore,
      maxScore: 40,
      ratingCode: report.performanceReview.performanceRatingCode?.code ?
        `${report.performanceReview.performanceRatingCode.code} (${report.performanceReview.performanceRatingCode.name})` :
        'N/A'
    },
    {
      key: '3',
      section: 'III',
      description: 'Overall Dynamic Organization Rating',
      percentage: '10%',
      score: typeof report.performanceReview.weightedDynamicOrganizationScore === 'object'
        ? report.performanceReview.weightedDynamicOrganizationScore.numericValue
        : report.performanceReview.weightedDynamicOrganizationScore,
      maxScore: 10,
      ratingCode: report.performanceReview.performanceRatingCode?.code ?
        `${report.performanceReview.performanceRatingCode.code} (${report.performanceReview.performanceRatingCode.name})` :
        'N/A'
    },
    {
      key: '4',
      section: 'TOTAL',
      description: 'TOTAL PERFORMANCE RATING',
      percentage: '100%',
      score: typeof report.performanceReview.totalPerformanceScore === 'object'
        ? report.performanceReview.totalPerformanceScore.numericValue
        : report.performanceReview.totalPerformanceScore,
      maxScore: 100,
      ratingCode: report.performanceReview.performanceRatingCode ?
        `${report.performanceReview.performanceRatingCode.code} (${report.performanceReview.performanceRatingCode.name})` :
        'N/A'
    }
  ] : [];
  const summaryColumns = [
    {
      title: 'Section',
      dataIndex: 'section',
      key: 'section',
      render: (text, record) => (
        <Text strong={record.key === '4'}>{text}</Text>
      )
    },
    {
      title: 'Section Description',
      dataIndex: 'description',
      key: 'description',
      render: (text, record) => (
        <Text strong={record.key === '4'}>{text}</Text>
      )
    },
    {
      title: 'Percentage of Total Rating',
      dataIndex: 'percentage',
      key: 'percentage',
      render: (text, record) => (
        <Text strong={record.key === '4'}>{text}</Text>
      )
    },
    {
      title: 'Score',
      dataIndex: 'score',
      key: 'score',
      render: (score, record) => (
        <InputNumber
          value={score}
          min={0}
          max={record.maxScore}
          precision={2}
          disabled
          style={{
            width: '100%',
            fontWeight: record.key === '4' ? 'bold' : 'normal'
          }}
        />
      )
    },
    {
      title: 'Rating Code',
      dataIndex: 'ratingCode',
      key: 'ratingCode',
      render: (text, record) => (
        <Text strong={record.key === '4'}>{text}</Text>
      )
    }
  ];
  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <Spin tip="Loading your reports..." size="large" />
      </div>
    );
  }
  if (error) {
    return (
      <Alert
        type="error"
        message="Error Loading Reports"
        description={error}
        showIcon
      />
    );
  }
  const hasDISC = report?.hasPersonalityReport;
  const hasReview = report?.hasPerformanceReview;
  const personality = report?.personalityReport || {};
  const lastUpdated = report?.performanceReview?.lastModifiedOn ||
    report?.personalityReport?.lastModifiedOn ||
    null;
  const performanceFactorsData = getPerformanceFactorsData();
  const skillsData = getSkillsData();
  return (
    <Card style={{ margin: 24 }} className="card-professional"
      title={` Development Reports of : ${firstName}  ${lastName}`}
      loading={loading}
      bordered
      extra={
        <Button type="default" onClick={() => navigate(-1)}>
          Back
        </Button>
      }
    >
      <Text type="secondary">
        Last updated: {lastUpdated ? new Date(lastUpdated).toLocaleDateString() : "Not available"}
      </Text>
      {/* DISC Section */}
      <Divider orientation="left" style={{ marginTop: 24 }}>
        Personality Assessment (DISC)
      </Divider>
      {hasDISC ? (
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Your Personality Type">
                <Tag color="blue" style={{ fontSize: 16 }}>
                  {personality.displayName} ({personality.typeCode})
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Summary">
                <Paragraph>{personality.summary}</Paragraph>
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col xs={24} md={12}>
            <div className="custom-section" style={{
              border: '1px solid #f0f0f0',
              borderRadius: '2px',
              padding: '16px',
              marginBottom: '16px',
              backgroundColor: '#fff'
            }}>
              <div style={{
                fontSize: '16px',
                fontWeight: '500',
                marginBottom: '12px',
                color: 'rgba(0, 0, 0, 0.85)',
                backgroundColor: '#f8f8f8',
                padding: '8px 12px',
                borderRadius: '2px'
              }}>
                Strengths
              </div>
              <List
                dataSource={personality.strengths}
                renderItem={(item) => (
                  <List.Item>
                    <Text strong>{item.title}:</Text> {item.description}
                  </List.Item>
                )}
              />
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div className="custom-section" style={{
              border: '1px solid #f0f0f0',
              borderRadius: '2px',
              padding: '16px',
              marginBottom: '16px',
              backgroundColor: '#fff'
            }}>
              <div style={{
                fontSize: '16px',
                fontWeight: '500',
                marginBottom: '12px',
                color: 'rgba(0, 0, 0, 0.85)',
                backgroundColor: '#f8f8f8',  // Light grey background
                padding: '8px 12px',
                borderRadius: '2px'
              }}>
                Development Tips
              </div>
              <List
                dataSource={personality.developmentTips}
                renderItem={(item) => (
                  <List.Item>
                    <Text strong>{item.title}:</Text> {item.description}
                  </List.Item>
                )}
              />
            </div>
          </Col>
          <Col span={24}>
            <Divider orientation="left">DISC Quadrant Scores</Divider>
            <Card style={{ marginBottom: 24 }}>
              <div style={{
                width: "100%",
                maxWidth: "600px",
                margin: "0 auto",
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gridTemplateRows: "1fr 1fr",
                aspectRatio: "1 / 1",
                border: "1px solid #f0f0f0"
              }}>
                {personality.quadrantScores?.sort((a, b) => a.quadrant - b.quadrant).map((q) => {
                  const quadrant = quadrantMap[q.quadrant];
                  return (
                    <div
                      key={q.quadrant}
                      style={{
                        borderRight: [1, 3].includes(q.quadrant) ? "1px solid #f0f0f0" : undefined,
                        borderBottom: [1, 2].includes(q.quadrant) ? "1px solid #f0f0f0" : undefined,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: "16px",
                        backgroundColor: quadrant.bgColor,
                      }}
                    >
                      <Text strong style={{ color: quadrant.color, marginBottom: 8 }}>
                        {quadrant.label}
                      </Text>
                      <Text strong style={{ fontSize: 18, marginBottom: 8 }}>
                        {q.scorePercentage}%
                      </Text>
                      <List
                        size="small"
                        dataSource={quadrant.traits}
                        renderItem={(trait) => (
                          <List.Item style={{ padding: '4px 0', color: '#666' }}>
                            {trait}
                          </List.Item>
                        )}
                      />
                    </div>
                  );
                })}
              </div>
            </Card>
            <Card style={{ marginBottom: 24 }}>
              <Title level={4} style={{ marginBottom: 16 }}>DISC Personality Model</Title>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Card
                    style={{
                      borderLeft: `3px solid ${quadrantMap[1].color}`,
                      backgroundColor: quadrantMap[1].bgColor,
                      marginBottom: 16
                    }}
                  >
                    <Text strong style={{ color: quadrantMap[1].color }}>Dominance</Text>
                    <Paragraph style={{ color: '#666', marginBottom: 0 }}>
                      Direct, results-oriented, competitive. Handles problems assertively.
                    </Paragraph>
                  </Card>
                </Col>
                <Col xs={24} sm={12}>
                  <Card
                    style={{
                      borderLeft: `3px solid ${quadrantMap[2].color}`,
                      backgroundColor: quadrantMap[2].bgColor,
                      marginBottom: 16
                    }}
                  >
                    <Text strong style={{ color: quadrantMap[2].color }}>Influence</Text>
                    <Paragraph style={{ color: '#666', marginBottom: 0 }}>
                      Outgoing, enthusiastic, optimistic. Strong communication and relational skills.
                    </Paragraph>
                  </Card>
                </Col>
                <Col xs={24} sm={12}>
                  <Card
                    style={{
                      borderLeft: `3px solid ${quadrantMap[3].color}`,
                      backgroundColor: quadrantMap[3].bgColor,
                      marginBottom: 16
                    }}
                  >
                    <Text strong style={{ color: quadrantMap[3].color }}>Steadiness</Text>
                    <Paragraph style={{ color: '#666', marginBottom: 0 }}>
                      Calm, patient, and dependable. Handles tasks thoughtfully.
                    </Paragraph>
                  </Card>
                </Col>
                <Col xs={24} sm={12}>
                  <Card
                    style={{
                      borderLeft: `3px solid ${quadrantMap[4].color}`,
                      backgroundColor: quadrantMap[4].bgColor,
                      marginBottom: 16
                    }}
                  >
                    <Text strong style={{ color: quadrantMap[4].color }}>Compliance</Text>
                    <Paragraph style={{ color: '#666', marginBottom: 0 }}>
                      Analytical, precise, systematic. Values structure and rules.
                    </Paragraph>
                  </Card>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      ) : (
        <Alert
          type="warning"
          message="Personality Assessment Pending"
          description={
            <>
              <Text>This Employer has not been done his DISC Assesssment.</Text>
            </>
          }
          showIcon
        />
      )}
      {/* Performance Review Section */}
      {hasReview && (
        <>
          <Divider orientation="left" style={{ marginTop: 24 }}>
            Performance Review
          </Divider>
          {/* Performance Visualization Section */}
          <div style={{ marginTop: 24 }}>
            <Title level={4}>Performance Visualization</Title>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
              {performanceFactorsData && (
                <div style={{ flex: 1, minWidth: 300, maxWidth: 500 }}>
                  <Title level={5} style={{ textAlign: 'center' }}>Performance Factors</Title>
                  <Radar
                    data={performanceFactorsData}
                    options={radarOptions}
                  />
                </div>
              )}
              {skillsData && (
                <div style={{ flex: 1, minWidth: 300, maxWidth: 500 }}>
                  <Title level={5} style={{ textAlign: 'center' }}>Skills Assessment</Title>
                  <Radar
                    data={skillsData}
                    options={radarOptions}
                  />
                </div>
              )}
            </div>
          </div>
          {/* Performance Evaluation Summary Section */}
          <div style={{
            border: "1px solid #d9d9d9",
            padding: "16px",
            margin: "24px 0",
            borderRadius: "6px",
            backgroundColor: "#fafafa"
          }}>
            <Title level={4} style={{ marginBottom: 16 }}>Performance Evaluation Summary</Title>
            <Table
              columns={summaryColumns}
              dataSource={summaryData}
              pagination={false}
              bordered
              size="small"
              rowClassName={(record) => record.key === '4' ? 'summary-total-row' : ''}
            />
          </div>
        </>
      )}
    </Card>
  );
};
export default PerformanceReviewDISC;